import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Plus, Trash2, History } from "lucide-react";

const VARS = ["{prenom}", "{nom}"];

export function SmsComposer({ selected, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [body, setBody] = useState("");
  const [tplName, setTplName] = useState("");
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/sms/templates").then(({ data }) => setTemplates(data));
    api.get("/admin/sms/config").then(({ data }) => setCfg(data));
    api.get("/admin/sms/log").then(({ data }) => setLog(data));
  };
  useEffect(load, []);

  const saveTemplate = async () => {
    if (!tplName || !body) return;
    await api.post("/admin/sms/templates", { name: tplName, body });
    setTplName(""); toast.success("Modèle enregistré"); load();
  };

  const removeTemplate = async (id) => { await api.delete(`/admin/sms/templates/${id}`); load(); };

  const send = async () => {
    setBusy(true);
    try {
      const names = Object.fromEntries(selected.map((c) => [c.phone, c.name]));
      const { data } = await api.post("/admin/sms/send", { to: selected.map((c) => c.phone), body, names });
      toast.success(`${data.sent} SMS envoyé(s)${data.failed ? `, ${data.failed} échec(s)` : ""}`);
      onSent?.(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const segments = Math.max(1, Math.ceil(body.length / (/[^\x00-\x7F]/.test(body) ? 70 : 160)));

  return (
    <div className="rounded-2xl border border-border bg-secondary/50 p-5 mt-4" data-testid="sms-composer">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-xl text-olive">Envoyer un SMS <span className="text-sm text-muted-foreground font-sans">· {selected.length} destinataire(s) sélectionné(s)</span></h3>
        {cfg && !cfg.configured && <Badge className="bg-rosebrand-pale text-rose-brand" data-testid="sms-not-configured">SMS non configuré (Administration → Brevo)</Badge>}
      </div>
      <div className="flex flex-col md:flex-row gap-2 mt-3">
        <Select onValueChange={(id) => setBody(templates.find((t) => t.template_id === id)?.body || "")}>
          <SelectTrigger className="md:w-64 bg-white" data-testid="sms-template-select"><SelectValue placeholder="Charger un modèle…" /></SelectTrigger>
          <SelectContent className="bg-white">
            {templates.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun modèle</div>}
            {templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1 flex-wrap items-center text-xs text-muted-foreground">
          Variables :
          {VARS.map((v) => <button key={v} onClick={() => setBody((b) => b + v)} className="px-2 py-0.5 rounded-full bg-white border border-border hover:border-olive" data-testid={`sms-var-${v}`}>{v}</button>)}
        </div>
      </div>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Bonjour {prenom}, votre logement L’Orne Park vous attend demain à partir de 16h…" className="mt-3 bg-white" data-testid="sms-body-input" />
      <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
        <span className="text-xs text-muted-foreground" data-testid="sms-counter">{body.length} caractères · {segments} SMS</span>
        <div className="flex gap-2 items-center flex-wrap">
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Nom du modèle" className="h-9 w-40 bg-white" data-testid="sms-template-name" />
          <Button variant="outline" size="sm" onClick={saveTemplate} disabled={!tplName || !body} className="border-olive text-olive" data-testid="sms-template-save"><Plus className="w-4 h-4 mr-1" /> Modèle</Button>
          <Button onClick={send} disabled={busy || !body || selected.length === 0} className="bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="sms-send-button">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Envoi…" : `Envoyer à ${selected.length}`}
          </Button>
        </div>
      </div>
      {templates.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {templates.map((t) => (
            <span key={t.template_id} className="inline-flex items-center gap-1 text-xs bg-white border border-border rounded-full pl-3 pr-1 py-0.5" data-testid={`sms-template-${t.template_id}`}>
              {t.name}
              <button onClick={() => removeTemplate(t.template_id)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <button onClick={() => setShowLog(!showLog)} className="mt-4 text-xs text-olive flex items-center gap-1 hover:underline" data-testid="sms-log-toggle"><History className="w-3.5 h-3.5" /> Historique ({log.length})</button>
      {showLog && (
        <div className="mt-2 max-h-56 overflow-y-auto divide-y divide-border text-xs bg-white rounded-xl border border-border" data-testid="sms-log">
          {log.length === 0 && <p className="p-3 text-muted-foreground">Aucun SMS envoyé.</p>}
          {log.map((l, i) => (
            <div key={i} className="flex justify-between gap-3 p-2">
              <span className="truncate"><strong>{l.name || l.to}</strong> · {l.body}</span>
              <span className={l.status === "failed" ? "text-destructive shrink-0" : "text-olive shrink-0"} title={l.error || ""}>{l.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
